import React, { useState } from 'react';
import { fdApi } from '../services/api';
import '../styles/Modal.css';

function GenerateFDAnalysisModal({ entity, onClose, onComplete }) {
  const [reportingDate, setReportingDate] = useState('');
  const [includeInterestSchedule, setIncludeInterestSchedule] = useState(false);
  const [includeTBReconciliation, setIncludeTBReconciliation] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [outputBlob, setOutputBlob] = useState(null);

  const handleCalculate = async () => {
    if (!reportingDate) {
      setError('Please enter a reporting date');
      return;
    }

    setCalculating(true);
    setError(null);
    setResult(null);

    try {
      const data = await fdApi.calculateFDAnalysis(
        entity.name,
        reportingDate,
        includeInterestSchedule,
        includeTBReconciliation
      );
      setResult(data);

      // Fetch the output file for immediate download option
      const blob = await fdApi.downloadFDOutput(
        entity.name,
        reportingDate,
        data.timestamp
      );
      setOutputBlob(blob);
    } catch (err) {
      setError(err.message || 'Failed to calculate FD analysis');
    } finally {
      setCalculating(false);
    }
  };

  const handleDownload = () => {
    if (!outputBlob || !result) return;

    const url = window.URL.createObjectURL(outputBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fd_analysis_${entity.name}_${result.reporting_date}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleClose = () => {
    if (result && onComplete) {
      onComplete();
    } else {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Generate FD Analysis</h2>
          <button className="modal-close" onClick={handleClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}

          {result && (
            <div className="alert alert-success">
              Successfully generated FD analysis!
              <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                <li>Reporting Date: {result.reporting_date}</li>
                <li>Total FDs: {result.summary.total_fds}</li>
                <li>Total Principal: ₹ {result.summary.total_principal.toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}</li>
                <li>Accrued Interest: ₹ {result.summary.total_accrued_interest.toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}</li>
                <li>Total Value: ₹ {result.summary.total_value.toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}</li>
              </ul>
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f0f8ff', borderRadius: '4px' }}>
                <strong>Classification:</strong>
                <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                  <li>Current (≤ 12 months): ₹ {result.summary.total_current.toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</li>
                  <li>Non-Current (&gt; 12 months): ₹ {result.summary.total_non_current.toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</li>
                </ul>
              </div>
              {result.tb_reconciliation && (
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: result.tb_reconciliation.reconciled ? '#e8f5e9' : '#fff3e0', borderRadius: '4px' }}>
                  <strong>TB Reconciliation:</strong> {result.tb_reconciliation.reconciled ? '✓ Reconciled' : '⚠ Not Reconciled'}
                  {!result.tb_reconciliation.reconciled && (
                    <div style={{ marginTop: '5px', fontSize: '0.9em' }}>
                      Difference: ₹ {Math.abs(result.tb_reconciliation.difference).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </div>
                  )}
                </div>
              )}
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                style={{ marginTop: '15px' }}
              >
                Download Analysis
              </button>
            </div>
          )}

          {!result && (
            <>
              <p>Calculate FD analysis for {entity.name}</p>

              <div className="form-group">
                <label htmlFor="reporting-date">Reporting Date</label>
                <input
                  type="date"
                  id="reporting-date"
                  className="form-control"
                  value={reportingDate}
                  onChange={(e) => setReportingDate(e.target.value)}
                  disabled={calculating}
                />
                <small className="form-text">
                  FDs will be classified as Current or Non-Current based on maturity date
                </small>
              </div>

              <div className="form-group" style={{ marginTop: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeInterestSchedule}
                    onChange={(e) => setIncludeInterestSchedule(e.target.checked)}
                    disabled={calculating}
                    style={{ marginRight: '8px' }}
                  />
                  <span>Include Interest Schedule</span>
                </label>
                <small className="form-text">
                  Generate detailed period-wise interest accrual schedule
                </small>
              </div>

              <div className="form-group" style={{ marginTop: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeTBReconciliation}
                    onChange={(e) => setIncludeTBReconciliation(e.target.checked)}
                    disabled={calculating}
                    style={{ marginRight: '8px' }}
                  />
                  <span>Include TB Reconciliation</span>
                </label>
                <small className="form-text">
                  Reconcile FD balances with Trial Balance GL accounts
                </small>
              </div>

              <div className="alert alert-info" style={{ marginTop: '15px' }}>
                <strong>Output includes:</strong>
                <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                  <li>FD Register with maturity analysis</li>
                  <li>Bank-wise summary</li>
                  <li>Schedule III disclosure format</li>
                  <li>Current vs Non-Current classification</li>
                  {includeInterestSchedule && <li>Interest accrual schedule</li>}
                  {includeTBReconciliation && <li>Trial Balance reconciliation</li>}
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {!result && (
            <button
              className="btn btn-primary"
              onClick={handleCalculate}
              disabled={!reportingDate || calculating}
            >
              {calculating ? 'Calculating...' : 'Generate'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GenerateFDAnalysisModal;
